import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MultipleChoiceQuestion, CheckboxQuestion, ShortAnswerQuestion, ParagraphQuestion, TextElement, ImageUploadQuestion, ImageUploadQuestionPreview, CopiableTextQuestionPreview, CopiableTextQuestion, TicketQuestionPreview, TicketQuestion, ResumPagamentsQuestionPreview, ResumPagamentsQuestion, ImagePreview, ImageElement, TicketV2Question, TicketV2QuestionPreview, TicketV2SingleQuestionPreview, TicketV2SingleQuestion, AssistanceQuestion, StripePaymentQuestion } from './FormElements';
import { MultipleChoiceQuestionPreview, CheckboxQuestionPreview, ShortAnswerQuestionPreview, ParagraphQuestionPreview, TextElementPreview, AssistanceQuestionPreview, StripePaymentQuestionPreview } from './FormElements';
import './FormEditorStyles.css';
import '../notifications/notifications.css'

import Styled from "styled-components";
import List from '../../utils/draggable-list';

import TimePickerDialog from '../other/TimePickerDialog';
import moment from 'moment';
import { fetchAPI, fetchAPIquery, postAPI, applyInverseTimeZone, getSubdomain } from '../../utils/utils';
import Popup from '../other/Popup';
import PopupFormDuplicate from './components/PopupFormDuplicate';
import { getCastellerDisplayName } from '../../utils/castellerName';
import { formatMoney } from '../../utils/money';
import { validateIban } from '../../utils/iban';
import { isDefaultIban } from '../../utils/treasuryWarnings';
import { NOTIFICATION_TITLE_MAX_LENGTH } from '../notifications/notificationLimits';

function DragHandle(props) {
    return (
        <div {...props} style={{ display: 'flex', justifyContent: 'space-evenly', flex: 1 }}>
            <div style={{ color: 'gray', fontSize: 28, backgroundColor: '#ccc', color: 'white', paddingLeft: 10, paddingRight: 10, borderRadius: 5, alignItems: 'center', display: 'flex' }}>
              ···
            </div>
        </div>
    )
}

const PreguntaItem = Styled.div`
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    border: solid 1px #ccc;
    border-radius: 5px;
    background-color: white;
`

const getTimeDiff = (start, end) => {
  const diff = Math.ceil(end.diff(start, 'minutes', true))
  const days = Math.floor(diff / (24*60));
  const hours = Math.floor((diff - days*24*60) / 60);
  const minutes = Math.floor(diff - days*24*60 - hours*60);

  if (diff >= 2*24*60) {
    return `${days} dies` + (hours > 0 ? ` i ${hours} hores` : '');
  } else if (diff >= 24*60) {
    return `1 dia` + (hours > 0 ? ` i ${hours} hores` : '');
  } else if (diff >= 2*60) {
    return `${hours} hores` + (minutes > 0 ? ` i ${minutes} minuts` : '');
  } else if (diff >= 60) {
    return `1 hora` + (minutes > 0 ? ` i ${minutes} minuts` : '');
  } else if (diff > 1) {
    return `${minutes} minuts`;
  } else if (diff === 1) {
    return `1 minut`;
  } else {
    return `alhora`;
  }
};

const normalizePriceForSave = (value) => {
  if (value === '' || value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'number') {
    return value;
  }

  const parsed = parseFloat(value.toString().replace(/,/g, '.'));
  return Number.isNaN(parsed) ? value : parsed;
};

const mapElementPricesForSave = (element) => {
  if (element.type === 'ticket') {
    return {
      ...element,
      price: normalizePriceForSave(element.price),
    };
  }

  if (element.type === 'ticket-v2' || element.type === 'ticket-v2-single') {
    return {
      ...element,
      content: {
        ...element.content,
        options: element.content.options.map(option => ({
          ...option,
          price: normalizePriceForSave(option.price),
        })),
      },
    };
  }

  return element;
};

const getOptionDisplayText = (option, index) => {
  if (typeof option === 'string') {
    return option;
  }

  if (option && typeof option === 'object') {
    if (typeof option.text === 'string' && option.text.trim() !== '') {
      return option.text;
    }

    return `Opció ${index + 1}`;
  }

  return `Opció ${index + 1}`;
};

const getTicketOptionSummary = (option, index) => {
  const label = getOptionDisplayText(option, index);

  if (option && typeof option === 'object' && option.price !== undefined && option.price !== null && option.price !== '') {
    return `${label} - ${formatMoney(option.price)}€`;
  }

  return label;
};

const CONDITIONAL_PARENT_TYPES = [
  'multiple-choice',
  'checkbox',
  'ticket',
  'ticket-v2',
  'ticket-v2-single',
  'assistance',
];

const canBeConditionalParent = (element) => (
  !!element &&
  !element.deleted &&
  CONDITIONAL_PARENT_TYPES.includes(element.type)
);

const getOrderedElements = (elements = [], order = []) => {
  const orderedElements = order
    .map(id => elements.find(element => element.id === id))
    .filter(Boolean);

  return [
    ...orderedElements,
    ...elements.filter(element => !order.includes(element.id)),
  ];
};

export const getConditionalParentQuestions = (elements = [], currentElementId, order = []) => {
  const orderedElements = getOrderedElements(elements, order);
  const currentIndex = orderedElements.findIndex(element => element.id === currentElementId);
  if (currentIndex === -1) return [];

  return orderedElements
    .filter((element, index) => index < currentIndex && element.content && element.content.question !== undefined)
    .filter(canBeConditionalParent);
};

export const sanitizeConditionalElements = (elements = [], order = []) => elements.map((element) => {
  if (!element.showIf) return element;

  const conditionalParents = getConditionalParentQuestions(elements, element.id, order);
  const parent = conditionalParents.find(candidate => candidate.id === element.showIf.questionId);

  if (parent) return element;

  return {
    ...element,
    showIf: null,
  };
});

// responseCount null: the count could not be read
export const deleteFormMessage = (responseCount) => {
  if (responseCount === 0) return 'Estàs segur que vols eliminar el formulari? No es podrà recuperar.';
  const answers = responseCount === 1 ? 'la resposta que té' : responseCount ? `les ${responseCount} respostes que té` : 'totes les respostes';
  return `Compte! Si elimines el formulari, s'eliminaran també ${answers}, i no es podran recuperar.\n\nSi vols un formulari nou per a aquest acte, pots afegir les preguntes noves a aquest.\n\nVols eliminar el formulari i les respostes?`;
};

const buildFormStorageKey = (eventId, userId) => {
  if (!eventId) return null;
  return `form-editor-draft:${userId || 'anon'}:${eventId}`;
};

const FORM_DRAFT_KEY_PREFIX = 'form-editor-draft:';
const FORM_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isDraftExpired = (draft) => {
  const savedAt = draft?.savedAt;
  if (!savedAt) return true;
  const savedAtMs = new Date(savedAt).getTime();
  if (Number.isNaN(savedAtMs)) return true;
  return (Date.now() - savedAtMs) > FORM_DRAFT_TTL_MS;
};

const readFormDraft = (storageKey) => {
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.formData) return null;
    if (isDraftExpired(parsed)) {
      window.localStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeFormDraft = (storageKey, formData) => {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      savedAt: new Date().toISOString(),
      formData,
    }));
  } catch (error) {
    // Ignore storage errors (quota, privacy mode, etc.)
  }
};

const clearFormDraft = (storageKey) => {
  if (!storageKey) return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    // Ignore storage errors (quota, privacy mode, etc.)
  }
};

const cleanupExpiredFormDrafts = () => {
  try {
    const keysToDelete = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key || !key.startsWith(FORM_DRAFT_KEY_PREFIX)) continue;
      keysToDelete.push(key);
    }

    keysToDelete.forEach((key) => {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
          window.localStorage.removeItem(key);
          return;
        }
        const parsed = JSON.parse(raw);
        if (isDraftExpired(parsed)) {
          window.localStorage.removeItem(key);
        }
      } catch (error) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (error) {
    // Ignore storage errors (quota, privacy mode, etc.)
  }
};

const FormPreview = ({ hidden, required, title, description, elements, order, socket, selectedEvent, notification, setNotification }) => {
  const sortedElements = elements
    .filter(el => !el.deleted)
    .sort((a, b) => order.indexOf(a.id) > order.indexOf(b.id) ? 1 : -1);

  const ComponentMap = {
    'multiple-choice': MultipleChoiceQuestionPreview,
    checkbox: CheckboxQuestionPreview,
    'short-answer': ShortAnswerQuestionPreview,
    paragraph: ParagraphQuestionPreview,
    'image-upload': ImageUploadQuestionPreview,
    'copiable-text': CopiableTextQuestionPreview,
    image: ImagePreview,
    'resum-pagaments': ResumPagamentsQuestionPreview,
    ticket: TicketQuestionPreview,
    text: TextElementPreview,
    'ticket-v2': TicketV2QuestionPreview,
    'ticket-v2-single': TicketV2SingleQuestionPreview,
    assistance: AssistanceQuestionPreview,
    'stripe-payment': StripePaymentQuestionPreview,
  };

  const renderedElements = sortedElements.map((element, index) => {
    const Component = ComponentMap[element.type];
    return <Component
      key={index}
      element={element}
      selectedEvent={selectedEvent}
    />;
  });

  return (
    <div className="form-preview">
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '10px' }}>
        <div>
          {
            hidden ? <em>🔒 Ocult</em> :
            <em>🌐 Visible</em>
          }
        </div>
        <div>
          <input type="checkbox" checked={required} disabled />
          <em>Obligatori</em>
        </div>
      </div>
      <h2>{title}</h2>
      <p>{description}</p>
      {renderedElements}
    </div>
  );
};

function MockupNotification({ ...props }) {
  // Create a HTML mockup of an Apple Push Notification
  const { title, body, data } = props;

  const notification_style = {
      width: '95%',
      backgroundColor: 'rgb(245,245,245)',
      borderRadius: '10px',
      boxShadow: '0 0 10px 0 rgba(0,0,0,0.5)',
      padding: '10px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
  };

  const notification_title_text_style = {
      fontSize: '1em',
      fontWeight: 'bold',
      color: 'black',
  };

  const notification_body_text_style = {
      fontSize: '0.8em',
      color: 'black',
  };

  const notification_icon_style = {
      width: '50px',
      height: '50px',
      borderRadius: 10,
      backgroundColor: 'white',
  };

  return (
      <div style={notification_style}>
          <div style={{ padding: 10 }}>
              <img style={notification_icon_style} src="/aleta-icon.png" alt="icon" />
          </div>
          <div style={{ padding: 10 }}>
              <div style={notification_title_text_style}>{title}</div>
              <div style={notification_body_text_style}>{body}</div>
          </div>
      </div>
  );
}

export const NotifyAlreadyVenen = ({ userInfo, socket, selectedEvent, castellersInfo, formTitle, selectedDay, selectedEventTitle, preloadedAssistencies, preloadedFormResponseUserIds }) => {
  const [sendingState, setSendingState] = useState(null);
    useSocketReconnect(socket, () => {
        if (selectedEvent) socket.emit('.request_form_responses', selectedEvent);
    });

  const [progressText, setProgressText] = useState('');

  const [assistencies, setAssistencies] = useState(preloadedAssistencies || []);
  const [responses, setResponses] = useState(preloadedFormResponseUserIds || []);

  const defaultTitle = '{nom}, tens pendent un formulari';
  const defaultBody = `Has dit que vens, però et falta respondre el formulari de "{esdeveniment}".`

  const [title, setTitle] = useState(defaultTitle);
  const [body, setBody] = useState(defaultBody);
  const titleHasReachedLimit = title.length >= NOTIFICATION_TITLE_MAX_LENGTH;

  const [collapseNotification, setCollapseNotification] = useState(true);

  // Sync from preloaded props when they arrive after mount
  useEffect(() => {
    if (preloadedAssistencies) setAssistencies(preloadedAssistencies);
  }, [preloadedAssistencies]);

  useEffect(() => {
    if (preloadedFormResponseUserIds) setResponses(preloadedFormResponseUserIds);
  }, [preloadedFormResponseUserIds]);

  useEffect(() => {
      if (!preloadedAssistencies) {
        fetchAPI(`/assistencies_event/${selectedEvent}`, data => {
          setAssistencies(data.data)
        })
      }

      socket.emit('.request_form_responses', selectedEvent)

      const onFormResponses = (res) => {
          if (res.evId === selectedEvent) {
              setResponses(Object.keys(res.responses))
          }
      }
      socket.on('.form_responses', onFormResponses)

      return () => {
          socket.off('.form_responses', onFormResponses);
      }
  }, [selectedEvent]);

  const venenAndNoResponse = assistencies
      .filter(row => row.assistencia === 'Vinc')
      .filter(row => !responses.map(id => parseInt(id)).includes(row.id))

  const format_text = (text, id) => {
      const nom = id in castellersInfo ?
          castellersInfo[id].nom
          : ''

      const sobrenom = id in castellersInfo ?
          castellersInfo[id].mote ? castellersInfo[id].mote :
          castellersInfo[id].nom ? castellersInfo[id].nom :
          ''
      : ''

      return text
          .replaceAll(' {nom}', nom !== '' ? ` ${nom}` : '')
          .replaceAll('{nom}', nom !== '' ? nom : '')
          .replaceAll(' {sobrenom}', sobrenom !== '' ? ` ${sobrenom}` : '')
          .replaceAll('{sobrenom}', sobrenom !== '' ? sobrenom : '')
          .replaceAll(' {titol}', formTitle !== '' ? ` ${formTitle}` : '')
          .replaceAll('{titol}', formTitle !== '' ? formTitle : '')
          .replaceAll(' {esdeveniment}', selectedEventTitle !== '' ? ` ${selectedEventTitle}` : '')
          .replaceAll('{esdeveniment}', selectedEventTitle !== '' ? selectedEventTitle : '')
  }

  const notifica_users = (users) => {
    const notification_id = uuidv4();

    console.log('[forms] Sending reminder notification', {
      selectedEvent,
      recipientsCount: users?.length || 0,
    });

    users.forEach(uid => 
        socket.emit('.send_notification_to_user',
            uid,
            {
                title: format_text(title, uid),
                body: format_text(body, uid),
                data: {
                  selectedDay: selectedDay,
                  notification_id: notification_id
                },
                notification_id: notification_id,
                author: userInfo.id,
            }
        )
    )
  }

  useEffect(() => {
    let interval = null;

    if (sendingState === 'sending') {
      const total = venenAndNoResponse.length;
      let count = 0;
      interval = setInterval(() => {
        count++;
        setProgressText(`Enviant notificació a ${count} castellers... (de ${total} totals)`);
        if (count === total) {
          setSendingState('sent');
          clearInterval(interval);
        }
      }, 100);
    }

    return () => clearInterval(interval);
  }, [sendingState, venenAndNoResponse.length]);

  const withNotis = venenAndNoResponse.filter(row => row.has_notifications)
  const withoutNotis = venenAndNoResponse.filter(row => !row.has_notifications)

  const [loopIndex, setLoopIndex] = useState(0)

  const shortDisplayName = id => getCastellerDisplayName(castellersInfo[id])

  return venenAndNoResponse.length > 0 ? (
    <div>
      <div style={{ backgroundColor: '#eee', borderRadius: 10, marginBottom: 20, padding: 20 }}>
          <h3>Notifica als que venen i no han respost</h3>

          {
            collapseNotification ? (
              <div>
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                  {
                    venenAndNoResponse.map(row => (
                      <div
                        key={row.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fff',
                          borderRadius: 5,
                          padding: 5,
                          margin: 3,
                          fontSize: 11,
                        }}
                      >
                        {
                          shortDisplayName(row.id)
                        }
                      </div>
                    ))
                  }
                </div>
                
                {
                  sendingState === 'sent' ? (
                    <div style={{ marginTop: 20, padding: 10 }}>
                    {(withNotis.length === 1) && <p style={{ fontSize: 16 }}>✅&nbsp;&nbsp;&nbsp;&nbsp;Notificació enviada correctament a <strong>{withNotis.length}</strong> casteller.</p>}
                    {(withNotis.length > 1) && <p style={{ fontSize: 16 }}>✅&nbsp;&nbsp;&nbsp;&nbsp;Notificació enviada correctament a <strong>{withNotis.length}</strong> castellers.</p>}
                    {(withoutNotis.length === 1) && <p style={{ fontSize: 16 }}>😔&nbsp;&nbsp;&nbsp;&nbsp;<strong>{withoutNotis.length}</strong> casteller no té l'app o no té les notificacions activades.</p>}
                    {(withoutNotis.length > 1) && <p style={{ fontSize: 16 }}>😔&nbsp;&nbsp;&nbsp;&nbsp;<strong>{withoutNotis.length}</strong> castellers no tenen l'app o no tenen les notificacions activades.</p>}
                    </div>
                  ) : sendingState === 'sending' ? (
                    <div style={{ marginTop: 20, padding: 10 }}>
                      <p style={{ fontSize: 16 }}>{progressText || 'Preparant notificació...'}</p>
                      <div className="loading" />
                      <div style={{ margin: '1rem' }} />
                    </div>
                  ) : (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <button
                        style={{
                          cursor: 'pointer',
                          width: '100%',
                          fontSize: 14,
                          backgroundColor: 'lightgreen',
                          color: '#333',
                          flex: 4,
                        }}
                        onClick={() => {
                          notifica_users(venenAndNoResponse.map(row => row.id));
                          setSendingState('sending');
                        }}
                      >
                        Envia a {venenAndNoResponse.length} castellers
                      </button>

                      <button
                        onClick={() => setCollapseNotification(false)}
                        style={{ flex: 1, borderRadius: 5, fontSize: 12, padding: 10, backgroundColor: 'lightblue', color: '#333', cursor: 'pointer' }}
                      >
                        Edita la notificació
                      </button>
                    </div>
                  )
                }
              </div>
            ) : (
              <>
              {
                sendingState === 'sent' ? (
                  <div style={{ marginTop: 20, padding: 10 }}>
                    {(withNotis.length === 1) && <p style={{ fontSize: 16 }}>✅&nbsp;&nbsp;&nbsp;&nbsp;Notificació enviada correctament a <strong>{withNotis.length}</strong> casteller.</p>}
                    {(withNotis.length > 1) && <p style={{ fontSize: 16 }}>✅&nbsp;&nbsp;&nbsp;&nbsp;Notificació enviada correctament a <strong>{withNotis.length}</strong> castellers.</p>}
                    {(withoutNotis.length === 1) && <p style={{ fontSize: 16 }}>😔&nbsp;&nbsp;&nbsp;&nbsp;<strong>{withoutNotis.length}</strong> casteller no té l'app o no té les notificacions activades.</p>}
                    {(withoutNotis.length > 1) && <p style={{ fontSize: 16 }}>😔&nbsp;&nbsp;&nbsp;&nbsp;<strong>{withoutNotis.length}</strong> castellers no tenen l'app o no tenen les notificacions activades.</p>}
                  </div>
                ) : sendingState === 'sending' ? (
                  <div style={{ marginTop: 20, padding: 10 }}>
                    <p style={{ fontSize: 16 }}>{progressText || 'Preparant notificació...'}</p>
                    <div className="loading" />
                    <div style={{ margin: '1rem' }} />
                  </div>
                ) : null
              }
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginBottom: 15 }}>
                  {
                    venenAndNoResponse.map(row => (
                      <div
                        key={row.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fff',
                          borderRadius: 5,
                          padding: 5,
                          margin: 3,
                          fontSize: 11,
                        }}
                      >
                        {
                          shortDisplayName(row.id)
                        }
                      </div>
                    ))
                  }
                </div>

            <button
                onClick={() => setCollapseNotification(true)}
                style={{ width: '100%', backgroundColor: 'lightblue', color: '#333', borderRadius: 5, fontSize: 12, cursor: 'pointer', marginBottom: 15 }}
              >
                Amaga opcions d'editar
              </button>

          <div className="form-notification-title-field">
            <label className="form-notification-title-label" htmlFor="form-notification-title">
              Títol de la notificació
            </label>
            <input
              id="form-notification-title"
              type="text"
              value={title}
              maxLength={NOTIFICATION_TITLE_MAX_LENGTH}
              aria-describedby="form-notification-title-feedback"
              onChange={(e) => setTitle(e.target.value.slice(0, NOTIFICATION_TITLE_MAX_LENGTH))}
              placeholder={`(Proposta:) ${defaultTitle}`}
              style={{ width: '100%', fontSize: 14, borderRadius: 5, borderWidth: 1, borderColor: '#ccc', padding: 5 }}
            />
            <div
              id="form-notification-title-feedback"
              className={`form-notification-title-feedback${titleHasReachedLimit ? ' at-limit' : ''}`}
            >
              <span aria-live="polite">
                {titleHasReachedLimit ? `Has arribat al màxim de ${NOTIFICATION_TITLE_MAX_LENGTH} caràcters.` : 'Títol breu perquè es vegi complet a la notificació.'}
              </span>
              <span className="form-notification-title-counter">
                {title.length}/{NOTIFICATION_TITLE_MAX_LENGTH} caràcters
              </span>
            </div>
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={`(Proposta:) ${defaultBody}`} style={{ width: '100%', fontSize: 12 }} />    
          <p style={{ fontSize: 10 }}>Pots utilitzar: <span style={{ color: 'darkblue' }}>{['{nom}', '{sobrenom}', '{titol}'].join(', ')}</span> als textos de la notificació.</p>

          <div style={{ marginBottom: 20 }}>
              <p style={{ flex: 1, fontSize: 12 }}>Aquesta és la notificació que rebran els {venenAndNoResponse.length} usuaris seleccionats. Si no estàs segur/a, prova a enviar-la a un grup petit de persones.</p>

              <div style={{ display: "flex", flexDirection: 'row' }}>
                  <div
                      style={{ flex: 1, fontSize: 14, textAlign: 'center', color: '#777', padding: 5, borderColor: '#777', borderWidth: 1, borderStyle: 'solid', borderRadius: 10 }}
                      onClick={() => setLoopIndex(prev => (prev - 1 + venenAndNoResponse.length)%venenAndNoResponse.length)}    
                  >Casteller anterior</div>
                  <div
                      style={{ flex: 1, fontSize: 14, textAlign: 'center', color: '#777', padding: 5, borderColor: '#777', borderWidth: 1, borderStyle: 'solid', borderRadius: 10 }}
                      onClick={() => setLoopIndex(prev => (prev + 1)%venenAndNoResponse.length)}    
                  >Següent casteller</div>
              </div>
          </div>

          {
            venenAndNoResponse.length > 0 && (
              <MockupNotification
                title={format_text(title || defaultTitle, venenAndNoResponse[loopIndex%venenAndNoResponse.length].id)}
                body={format_text(body || defaultBody, venenAndNoResponse[loopIndex%venenAndNoResponse.length].id)}
              />
            )
          }

          <button
            style={{
              cursor: 'pointer',
              marginTop: '1rem',
              width: '100%',
              fontSize: 16,
              backgroundColor: 'lightgreen',
              color: '#333',
            }}
            onClick={() => {
              const recipients = venenAndNoResponse.map(row => row.id);
              console.log('[forms] Clicking send in edit mode', { recipientsCount: recipients.length });
              notifica_users(recipients);
              setSendingState('sending');
            }}
          >
            Envia a {venenAndNoResponse.length} castellers
          </button>
            </>
          ) }
        </div>
    </div>
  ) : (
    null
  )

} 

const FormEditor = ({ forms, events, userInfo, selectedDay, castellersInfo, readonly, socket, selectedEvent, notification, setNotification, preview, setPreview, preloadedAssistencies, preloadedFormResponseUserIds }) => {
  const initialLoadPending = useRef(true);
  useSocketReconnect(socket, () => {
    if (selectedEvent && initialLoadPending.current) socket.emit('.load_form', selectedEvent, userInfo?.id);
  });
  // Precompute initial form data to render immediately without "Carregant..." flash
  const _preloadedForm = forms?.[selectedEvent];
  const _preloadedDraft = readFormDraft(buildFormStorageKey(selectedEvent, userInfo?.id));
  const _initData = _preloadedDraft?.formData || _preloadedForm || {};

  const [title, setTitle] = useState(_initData.title || '');
  const [description, setDescription] = useState(_initData.description || '');
  const [elements, setElements] = useState(() => sanitizeConditionalElements(_initData.elements || [], _initData.order || []));
  const [errors, setErrors] = useState({});
  const [modeReorder, setModeReorder] = useState(false);
  const [order, setOrder] = useState(_initData.order || []);
  const [showLoadSaveButtons, setShowLoadSaveButtons] = useState(false);
  const [isNew, setIsNew] = useState(_preloadedForm ? !!_preloadedForm.new : null);
  const [required, setRequired] = useState(_preloadedForm ? ('required' in _initData ? _initData.required : true) : null);
  const [hidden, setHidden] = useState(!!_initData.hidden);
  // This form's payment options: accounts of its own and cash (see form-auto-payment.js)
  const [payment, setPayment] = useState(normalizePayment(_initData.payment));
  // Aleta makes the payment page (backend/form-auto-payment.js); on by default for new forms
  const [hasPaymentWarnings, setHasPaymentWarnings] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(null); // null = loading, true/false = result
  const hasLoadedRef = useRef(false);
  const isHydratingRef = useRef(false);
  const baselineSnapshotRef = useRef('');

  useEffect(() => {
    cleanupExpiredFormDrafts();
  }, []);

  const handleTitleChange = (newTitle) => setTitle(newTitle);
  const handleDescriptionChange = (newDescription) => setDescription(newDescription);

  const [openOpeningDateDialog, setOpenOpeningDateDialog] = useState(false);
  const [openClosingDateDialog, setOpenClosingDateDialog] = useState(false);
  const [openingDate, setOpeningDate] = useState(_initData.openingDate ? moment(_initData.openingDate) : null);
  const [closingDate, setClosingDate] = useState(_initData.closingDate ? moment(_initData.closingDate) : null);
  const [focusedInput, setFocusedInput] = useState(null);

  const normalizeLimitValue = (limit) => (limit === '' || limit === undefined ? null : limit);

  const normalizeElementsLimits = (elements = []) => elements.map((element) => {
    const normalizedElementLimit = normalizeLimitValue(element.limit);

    if (element.type !== 'ticket-v2' && element.type !== 'ticket-v2-single') {
      return { ...element, limit: normalizedElementLimit };
    }

    const normalizedOptions = (element.content?.options || []).map((option) => ({
      ...option,
      limit: normalizeLimitValue(option?.limit),
    }));

    return {
      ...element,
      limit: normalizedElementLimit,
      content: { ...element.content, options: normalizedOptions },
    };
  });

  // The payment elements Aleta makes are not edited here: the backend makes them again on every save
  const isAutoPaymentElement = (element) => element?.autoPayment === true;
  const serializeFormData = (data = {}) => {
    const autoIds = new Set((data.elements || []).filter(isAutoPaymentElement).map((element) => element.id));
    const ownElements = (data.elements || []).filter((element) => !isAutoPaymentElement(element));
    const ownOrder = (data.order || []).filter((id) => !autoIds.has(id));
    return {
    title: data.title || '',
    description: data.description || '',
    elements: sanitizeConditionalElements(normalizeElementsLimits(ownElements), ownOrder),
    order: ownOrder,
    required: 'required' in data ? data.required : true,
    hidden: !!data.hidden,
    payment: normalizePayment(data.payment),
    openingDate: data.openingDate ? data.openingDate : null,
    closingDate: data.closingDate ? data.closingDate : null,
    };
  };

  const getSerializedCurrentFormData = useCallback(() => serializeFormData({
    title,
    description,
    elements,
    order,
    required,
    hidden,
    payment,
    openingDate: openingDate?.isValid() ? openingDate.toISOString() : null,
    closingDate: closingDate?.isValid() ? closingDate.toISOString() : null,
  }), [title, description, elements, order, required, hidden, payment, openingDate, closingDate]);

  const buildSnapshotFromData = (data = {}) => {
    const normalized = serializeFormData(data);
    return JSON.stringify(normalized);
  };

  const buildSnapshotFromState = () => JSON.stringify(getSerializedCurrentFormData());

  const clearDraftAndReloadForm = useCallback(() => {
    const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
    clearFormDraft(storageKey);
    socket.emit('.load_form', selectedEvent, userInfo?.id);
  }, [selectedEvent, userInfo?.id, socket]);

  const applyFormDataToState = (data = {}) => {
    const normalized = serializeFormData(data);
    setTitle(normalized.title);
    setDescription(normalized.description);
    setElements(normalized.elements);
    setOrder(normalized.order);
    setRequired(normalized.required);
    setHidden(normalized.hidden);
    setPayment(normalized.payment);
    setOpeningDate(normalized.openingDate ? moment(normalized.openingDate) : null);
    setClosingDate(normalized.closingDate ? moment(normalized.closingDate) : null);
  };

  const resetFormToEmpty = () => {
    applyFormDataToState({
      title: '',
      description: '',
      elements: [],
      order: [],
      required: true,
      hidden: false,
      openingDate: null,
      closingDate: null,
    });
    setErrors({});
    setHasPaymentWarnings(false);
    setModeReorder(false);
    setShowLoadSaveButtons(false);
  };

  const handleCancelCreate = () => {
    const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
    const draft = readFormDraft(storageKey);
    if (draft?.formData) {
      const confirmation = window.confirm('Vols borrar tots els canvis que has fet al formulari?');
      if (!confirmation) return;
      clearFormDraft(storageKey);
    }
    resetFormToEmpty();
    setPreview(true);
  };

  const addElement = (elementType) => {
    const ID = uuidv4();
    const type = elementType === 'comprovant-upload' ? 'image-upload' : elementType;

    setElements([
      ...elements,
      {
        id: ID,
        type: type,
        content:
          type === 'text' ? '' :
          type === 'assistance' ? { question: '', eventId: selectedEvent || '', yesLabel: 'Sí', noLabel: 'No' } :
          type === 'ticket' ? { question: '', options: ['', ''] } :
          type === 'ticket-v2' || type === 'ticket-v2-single' ? { question: '', options: [{ text: '', price: 0 }] } :
          type === 'stripe-payment' ? { buttonText: 'Pagar amb targeta', commissionMode: 'user_pays' } :
          elementType === 'comprovant-upload' ? { question: 'Penja el comprovant del pagament' } :
          { question: '', options: [''] },
        price: type === 'ticket' ? 0 : undefined,
        limit: type === 'ticket' ? null : undefined,
        required: (elementType === 'comprovant-upload' || type === 'ticket' || type === 'assistance') ? true : false,
        isComprovant: elementType === 'comprovant-upload',
        skipWhenNoPay: elementType === 'comprovant-upload' ? true : undefined,
        paymentScreen: false,
        showIf: null,
      },
    ]);

    setOrder([...order, ID]);
  };

  const updateElement = (id, updatedElement) => {
    setElements(
      elements.map((element, i) => (element.id === id ? updatedElement : element)),
    );

    // Mark form as having unsaved changes whenever an element is updated
    setShowLoadSaveButtons(true);

    // Run check for payment warnings only
    setTimeout(() => checkForPaymentWarningsOnly(), 0);
  };

  const removeElement = (id) => {
    if (window.confirm('Estàs segur que vols eliminar aquest element?')) {
      setElements(elements.map(element => {
        if (element.id !== id) {
          return element;
        }

        return {
          ...element,
          deleted: true,
          deletedAt: element.deletedAt || new Date().toISOString(),
        };
      }));
      setShowLoadSaveButtons(true);

      return true;
    } else {
      return false;
    }
  };

  const checkForPaymentWarnings = useCallback((element) => {
    if (element.type === 'multiple-choice' || element.type === 'checkbox') {
      const paymentTerms = ['€', 'euro', 'euros', 'preu', 'pagament', 'cost', 'pagar', 'import'];
      
      // Check question title for payment terms
      const titleContainsPaymentTerm = paymentTerms.some(term => 
        element.content.question.toLowerCase().includes(term.toLowerCase())
      );
      
      // Check any option for payment terms
      const anyOptionContainsPaymentTerm = element.content.options.some(option =>
        paymentTerms.some(term => option.toLowerCase().includes(term.toLowerCase()))
      );
      
      if (titleContainsPaymentTerm || anyOptionContainsPaymentTerm) {
        return {
          question: {
            message: "💡 Suggeriment: Aquesta pregunta sembla relacionada amb pagaments. Podries utilitzar un element de 'Pagament' o 'Pagament amb quantitats' per poder escanejar tiquets amb l'Aleta, demanar comprovants i només deixar apuntar-se els que han pagat.",
            isWarning: true
          }
        };
      }
    }
    return null;
  }, []);

  const checkForPaymentWarningsOnly = useCallback(() => {
    let foundPaymentWarnings = false;

    elements.filter(el => !el.deleted).forEach((element) => {
      if (element.type === 'multiple-choice' || element.type === 'checkbox') {
        const warning = checkForPaymentWarnings(element);
        if (warning) {
          foundPaymentWarnings = true;
        }
      }
    });
    
    setHasPaymentWarnings(foundPaymentWarnings);
  }, [elements, checkForPaymentWarnings]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    let foundPaymentWarnings = false;
    const paymentWarnings = {};

    elements.filter(el => !el.deleted).forEach((element) => {
      if (element.type !== 'image' && element.type !== 'resum-pagaments' && element.type !== 'text' && element.type !== 'stripe-payment' && element.content.question.trim() === '') {
        newErrors[element.id] = { question: 'El títol de la pregunta no pot estar buit.' };
      }
      if (element.type === 'multiple-choice' || element.type === 'checkbox' || element.type === 'ticket') {
        // Check if the question or any option contains payment terms
        if ((element.type === 'multiple-choice' || element.type === 'checkbox')) {
          const warning = checkForPaymentWarnings(element);
          if (warning) {
            if (!paymentWarnings[element.id]) {
              paymentWarnings[element.id] = {};
            }
            
            // Add warning to the question field
            paymentWarnings[element.id] = warning;
            foundPaymentWarnings = true;
          }
        }
        
        // Check for empty options (actual errors)
        element.content.options.forEach((option, optionIndex) => {
          if (option.trim() === '') {
            if (!newErrors[element.id]) {
              newErrors[element.id] = {};
            }
            newErrors[element.id][`option_${optionIndex}`] = "L'opció no pot estar buida, borra-la.";
          }
        });
      }
      if (element.type === 'ticket-v2' || element.type === 'ticket-v2-single') {
        element.content.options.forEach((option, optionIndex) => {
          if (option.hidden) return;
          if (option.text.trim() === '') {
            if (!newErrors[element.id]) {
              newErrors[element.id] = {};
            }
            newErrors[element.id][`option_${optionIndex}`] = "El text de l'opció no pot estar buit.";
          }

          if (option.price === undefined || option.price === null || option.price === '' || isNaN(option.price)) {
            if (!newErrors[element.id]) {
              newErrors[element.id] = {};
            }
            newErrors[element.id][`option_${optionIndex}`] = "El preu ha de ser un número vàlid (pot ser 0).";
          }
        });
      }
      
      if (element.type === 'text' && element.content.trim() === '') {
        newErrors[element.id] = { text: 'El text no pot estar buit.' };
      }

      if (element.type === 'copiable-text' && (!element.content.text || element.content.text.trim() === '')) {
        if (!newErrors[element.id]) {
          newErrors[element.id] = {};
        }
        newErrors[element.id].text = 'El text per copiar no pot estar buit.';
      }

      if (element.type === 'assistance') {
        if (!element.content.eventId) {
          if (!newErrors[element.id]) newErrors[element.id] = {};
          newErrors[element.id].eventId = 'Has de triar un esdeveniment';
        }
        if (!element.content.yesLabel || element.content.yesLabel.trim() === '') {
          if (!newErrors[element.id]) newErrors[element.id] = {};
          newErrors[element.id].yesLabel = 'El text per al sí no pot estar buit.';
        }
        if (!element.content.noLabel || element.content.noLabel.trim() === '') {
          if (!newErrors[element.id]) newErrors[element.id] = {};
          newErrors[element.id].noLabel = 'El text per al no no pot estar buit.';
        }
      }
    });
  
    // Combine errors and warnings for display, but only use actual errors for validation
    const combinedErrors = { ...newErrors };
    
    // Add payment warnings to the combined errors for display
    Object.keys(paymentWarnings).forEach(elementId => {
      if (!combinedErrors[elementId]) {
        combinedErrors[elementId] = {};
      }
      Object.keys(paymentWarnings[elementId]).forEach(key => {
        combinedErrors[elementId][key] = paymentWarnings[elementId][key];
      });
    });
    
    // Convert regular errors to the same format for consistency
    Object.keys(newErrors).forEach(elementId => {
      Object.keys(newErrors[elementId]).forEach(key => {
        if (typeof combinedErrors[elementId][key] === 'string') {
          combinedErrors[elementId][key] = {
            message: newErrors[elementId][key],
            isWarning: false
          };
        }
      });
    });
    
    setErrors(combinedErrors);
    setHasPaymentWarnings(foundPaymentWarnings);
    
    // Only return false if there are actual errors (not warnings)
    return Object.keys(newErrors).length === 0;
  }, [elements, checkForPaymentWarnings]);

  useEffect(() => {
    hasLoadedRef.current = false;
    isHydratingRef.current = true;
    initialLoadPending.current = true;
    socket.emit('.load_form', selectedEvent, userInfo?.id);

    const onLoadedForm = (loadedData) => {
      initialLoadPending.current = false;
      const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
      const draft = readFormDraft(storageKey);
      const dataToApply = draft?.formData || loadedData;

      isHydratingRef.current = true;
      applyFormDataToState(dataToApply);
      setIsNew(!!loadedData?.new);
      if (loadedData?.new && draft?.formData) {
        setPreview(false);
      }
      baselineSnapshotRef.current = buildSnapshotFromData(loadedData);
      const appliedSnapshot = buildSnapshotFromData(dataToApply);
      const hasChanges = appliedSnapshot !== baselineSnapshotRef.current;
      const opening = dataToApply.openingDate ? moment(dataToApply.openingDate) : null;
      const closing = dataToApply.closingDate ? moment(dataToApply.closingDate) : null;
      const invalidDates = opening?.isValid() && closing?.isValid() && opening.isAfter(closing);

      setTimeout(() => {
        isHydratingRef.current = false;
        hasLoadedRef.current = true;
        setShowLoadSaveButtons(!invalidDates && hasChanges);
      }, 0);
    };
    socket.on('.loaded_form', onLoadedForm);
  
    const onFormDeleted = () => {
      socket.emit('.load_form', selectedEvent, userInfo?.id);
    };
    socket.on('.form_deleted', onFormDeleted);

    return () => {
      socket.off('.loaded_form', onLoadedForm);
      socket.off('.form_deleted', onFormDeleted);
    };
  }, [
    selectedEvent,
    userInfo,
  ]);

  // Apply preloaded form data from get_all_forms to avoid "Carregant..." flash
  useEffect(() => {
    const formData = forms?.[selectedEvent];
    if (isNew === null && formData) {
      const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
      const draft = readFormDraft(storageKey);
      const dataToApply = draft?.formData || formData;

      isHydratingRef.current = true;
      applyFormDataToState(dataToApply);
      setIsNew(!!formData?.new);
      if (formData?.new && draft?.formData) {
        setPreview(false);
      }
      baselineSnapshotRef.current = buildSnapshotFromData(formData);
      const appliedSnapshot = buildSnapshotFromData(dataToApply);
      const hasChanges = appliedSnapshot !== baselineSnapshotRef.current;
      const opening = dataToApply.openingDate ? moment(dataToApply.openingDate) : null;
      const closing = dataToApply.closingDate ? moment(dataToApply.closingDate) : null;
      const invalidDates = opening?.isValid() && closing?.isValid() && opening.isAfter(closing);

      setTimeout(() => {
        isHydratingRef.current = false;
        hasLoadedRef.current = true;
        setShowLoadSaveButtons(!invalidDates && hasChanges);
      }, 0);
    }
  }, [forms, selectedEvent, isNew]);

  const selectedEventTitle = events
    ?.find(ev => ev.id === selectedEvent)
    ?.title

  useEffect(() => {
    const onFormSaved = res => {
      if (res.success) {
        const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
        clearFormDraft(storageKey);
        baselineSnapshotRef.current = buildSnapshotFromState();

        const defaultTitle =
          title ? `{nom}, hem canviat "${title}"` :
          selectedEventTitle ? `{nom}, hem canviat el formulari de "${selectedEventTitle}"` :
          `{nom}, hem canviat un formulari`

        const defaultBody =
          title ? `El formulari "${title}", que ja has respós, té noves preguntes. Si us plau, torna a respondre'l` :
          selectedEventTitle ? `El formulari de "${selectedEventTitle}", que ja has respós, té noves preguntes. Si us plau, torna a respondre'l` :
          `Un formulari que ja has respós té noves preguntes. Si us plau, torna a respondre'l`

        setNotification({
          msg: 'Formulari guardat correctament.',
          link: `/notifications?ev=${selectedEvent}&subgroup=venen&titol=${encodeURIComponent(defaultTitle)}&missatge=${encodeURIComponent(defaultBody)}`,
          linkText: 'Notifica el canvi als que ja han respós ↗',
          timeout: 5000,
        })
      } else {
        setNotification({
          msg: 'Algo ha fallat. Formulari guardat incorrectament...'
        })
      }
    }
    socket.on('.form_saved', onFormSaved)

    return () => {
      socket.off('.form_saved', onFormSaved);
    }
  }, [
    selectedEvent,
    title,
    userInfo?.id,
  ])
  
  useEffect(() => {
    if (!hasLoadedRef.current || isHydratingRef.current) return;

    const invalidDates = openingDate?.isValid() && closingDate?.isValid() && openingDate.isAfter(closingDate);
    const currentSnapshot = buildSnapshotFromState();
    const hasChanges = currentSnapshot !== baselineSnapshotRef.current;
    const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);

    if (hasChanges) {
      writeFormDraft(storageKey, getSerializedCurrentFormData());
    } else {
      clearFormDraft(storageKey);
    }

    setShowLoadSaveButtons(!invalidDates && hasChanges);
  }, [hidden, title, description, elements, order, required, payment, openingDate, closingDate, selectedEvent, userInfo?.id, getSerializedCurrentFormData]);

  // Validate form whenever elements change to keep hasPaymentWarnings up to date
  useEffect(() => {
    // Only run payment warnings check if there are elements to validate
    if (elements.length > 0) {
      checkForPaymentWarningsOnly();
    }
  }, [elements, checkForPaymentWarningsOnly]);

  // Check if Stripe Connect is configured and verified
  useEffect(() => {
    fetchAPI('/stripe/connect/status', data => {
      if (data?.success) {
        setStripeConfigured(data.configured && data.verified);
      } else {
        setStripeConfigured(false);
      }
    }, false);
  }, []);

  const saveForm = (force=true) => {
    if (validateForm()) {
      const elementsToSave = sanitizeConditionalElements(elements, order).map(mapElementPricesForSave);
      const formData = {
        title,
        description,
        elements: elementsToSave,
        order,
        required,
        hidden,
        payment,
        openingDate: (openingDate && openingDate?.isValid()) ? openingDate?.toISOString() : null,
        closingDate: (closingDate && closingDate?.isValid()) ? closingDate?.toISOString() : null,
      };

      socket.emit('.force_save_form', formData, selectedEvent, userInfo?.id);
      setShowLoadSaveButtons(false);

      socket.emit('.request_form_responses', selectedEvent)
      socket.emit('.load_form', selectedEvent, userInfo?.id)
    } else {
      const errorDetails = Object.entries(errors)
        .filter(([, fieldErrors]) => Object.values(fieldErrors).some(e => !e.isWarning))
        .map(([elementId, fieldErrors]) => {
          const el = elements.find(e => e.id === elementId);
          const label = el?.content?.question || el?.content || '(sense títol)';
          const msgs = Object.values(fieldErrors).filter(e => !e.isWarning).map(e => e.message);
          return `- ${typeof label === 'string' ? label.substring(0, 40) : '(element)'}: ${msgs.join(', ')}`;
        })
        .join('\n');
      alert(`Hi ha errors al formulari:\n\n${errorDetails || 'Revisa els missatges d\'error.'}`)
    }
  };

  const loadForm = () => {
    clearDraftAndReloadForm();
  };

  // Ask with the number of answers that go with the form, read fresh from the server
  const deleteForm = () => {
    fetchAPI(`/form_responses/${selectedEvent}`,
      (data) => confirmDeleteForm(Object.keys(data?.responses || {}).length),
      false,
      () => confirmDeleteForm(null));
  };

  const confirmDeleteForm = (responseCount) => {
    const confirmation = window.confirm(deleteFormMessage(responseCount));
    if (confirmation) {
      const storageKey = buildFormStorageKey(selectedEvent, userInfo?.id);
      clearFormDraft(storageKey);
      // Remove all tickets
      elements
        .filter(element => element.type === 'ticket' || element.type === 'ticket-v2' || element.type === 'ticket-v2-single')
        .forEach(element => postAPI('/remove_tiquet', { id: element.id }, () => {}))

      socket.emit('.delete_form', selectedEvent, userInfo?.id);
    }
  };

  const orderedElements = [...elements]
    .filter(element => !element.deleted)
    .sort((a, b) => order.indexOf(a.id) > order.indexOf(b.id) ? 1 : -1);

  // Something to pay, so the form gets Aleta's payment page
  const priceOf = (value) => parseFloat(String(value ?? '').replace(',', '.')) || 0;
  const hasPrices = orderedElements.some((element) => (element.type === 'ticket' && priceOf(element.price) > 0)
    || (['ticket-v2', 'ticket-v2-single'].includes(element.type)
      && (element.content?.options || []).some((option) => !option?.hidden && priceOf(option?.price) > 0)));

  // One list: the payment page is Aleta's (see form-auto-payment.js in the backend)
  const ElementsToEdit = orderedElements
    .map((element) => (
            <FormElement
                key={element.id}
                element={element}
                updateElement={(updatedElement) => updateElement(element.id, updatedElement)}
                removeElement={() => { return removeElement(element.id); }}
                errors={errors[element.id] || {}}
                selectedEvent={selectedEvent}
                allElements={orderedElements}
                events={events}
            />
        ))

  const [closedDuplicateFormPopup, setClosedDuplicateFormPopup] = useState(true);
        
  const duplicateForm = (fromEvent, idsToDuplicate=[]) => {
    if (fromEvent) {
      const targetFormData = getSerializedCurrentFormData();

      postAPI(
        `/duplicate_form/${fromEvent}/${selectedEvent}`,
        { idsToDuplicate, targetFormData },
        clearDraftAndReloadForm
      )
    }
  }

  return readonly ? (
    <FormPreview
        title={title}
        description={description}
        elements={elements}
        hidden={hidden}
        order={order}
        socket={socket}
        selectedEvent={selectedEvent}
        notification={notification}
        setNotification={setNotification}
        required={required}
    />
  ) : isNew === null ? (
    <div>
      <h4>Carregant...</h4>
    </div>
  ) : (isNew && preview) ? (
    <div>
      <PopupFormDuplicate
        closed={closedDuplicateFormPopup}
        setClosed={setClosedDuplicateFormPopup}
        events={events}
        forms={forms}
        duplicateForm={duplicateForm}
        selectedEvent={selectedEvent}
      />

      <h2>Crea un formulari</h2>
      <button
        onClick={() => setPreview(false)}
        style={{
          cursor: 'pointer',  
          marginBottom: '1rem',
          width: '100%',
          fontSize: 16,
        }}
      >
        + Crea un formulari
      </button>
      <button
        onClick={() => setClosedDuplicateFormPopup(false)}
        style={{
          cursor: 'pointer',  
          marginBottom: '1rem',
          width: '100%',
          fontSize: 16,
        }}
      >
        + Importar preguntes d'un altre formulari
      </button>
    </div>
  ) :
  preview ? (
    <div>
      <h2>Previsualització del formulari</h2>
      <NotifyAlreadyVenen
        socket={socket}
        castellersInfo={castellersInfo}
        formTitle={title}
        selectedDay={selectedDay}
        selectedEvent={selectedEvent}
        userInfo={userInfo}
        selectedEventTitle={selectedEventTitle}
        preloadedAssistencies={preloadedAssistencies}
        preloadedFormResponseUserIds={preloadedFormResponseUserIds}
      />

      <button
        onClick={() => setPreview(false)}
        style={{
          cursor: 'pointer',
          marginBottom: '1rem',
          width: '100%',
          fontSize: 16,
        }}
      >
        📝 Editar formulari
      </button>

      <FormPreview
        title={title}
        description={description}
        elements={elements}
        order={order}
        socket={socket}
        selectedEvent={selectedEvent}
        notification={notification}
        setNotification={setNotification}
        required={required}
        hidden={hidden}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button
          className='delete-button'
          onClick={deleteForm}
        >
          Esborra el formulari
        </button>
      </div>
    </div>
  ) :
  (
    <div>
      <PopupFormDuplicate
        closed={closedDuplicateFormPopup}
        setClosed={setClosedDuplicateFormPopup}
        events={events}
        forms={forms}
        duplicateForm={duplicateForm}
        selectedEvent={selectedEvent}
      />

      <h2>{isNew ? 'Crea un' : 'Edita el'} formulari</h2>
  
      {
        !isNew && (
          <NotifyAlreadyVenen
            socket={socket}
            castellersInfo={castellersInfo}
            formTitle={title}
            selectedDay={selectedDay}
            selectedEvent={selectedEvent}
            selectedEventTitle={selectedEventTitle}
            preloadedAssistencies={preloadedAssistencies}
            preloadedFormResponseUserIds={preloadedFormResponseUserIds}
          />
        )
      }

      <button
        onClick={isNew ? handleCancelCreate : () => setPreview(true)}
        style={{
          cursor: 'pointer',
          marginBottom: '1rem',
          width: '100%',
          fontSize: 16,
          backgroundColor: '#eee',
          color: 'black'
        }}
      >
        {
          !isNew ? <>👁️ Previsualitza el formulari</> :
          <>🚫 Cancel·lar la creació</>
        }
      </button>

      {hasPaymentWarnings && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '12px',
          marginBottom: '1rem',
          borderRadius: '5px',
          border: '1px solid #ffeeba'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>💡 Suggeriment</p>
          <p style={{ margin: '5px 0 0 0' }}>
            Has inclòs termes relacionats amb pagaments (com "€", "euros", "preu", etc.) en algunes opcions de selecció. 
            Si vols crear un formulari amb pagaments, podries utilitzar els elements de "Pagament" o "Pagament amb quantitats" 
            que trobaràs a la secció de Pagaments per una millor experiència d'usuari.
          </p>
        </div>
      )}

      <TitleInput title={title} handleTitleChange={handleTitleChange} />
      <DescriptionInput description={description} handleDescriptionChange={handleDescriptionChange} />

      <TimePickerDialog
        onConfirm={(date) => setOpeningDate(prevDate => {
          const [HH, MM] = date.split(':').map((n) => parseInt(n, 10));
          return moment(prevDate)
            .hour(HH)
            .minute(MM)
        })}
        title="Tria la data d'obriment"
        openDialog={openOpeningDateDialog}
        setOpenDialog={setOpenOpeningDateDialog}
      />

      <TimePickerDialog
        onConfirm={(date) => setClosingDate(prevDate => {
          const [HH, MM] = date.split(':').map((n) => parseInt(n, 10));
          return moment(prevDate)
            .hour(HH)
            .minute(MM)
        })}
        title="Tria la data de tancament"
        openDialog={openClosingDateDialog}
        setOpenDialog={setOpenClosingDateDialog}
      />

      <h3>Tria la data d'obriment i tancament</h3>

      {
        openingDate?.isValid() && closingDate?.isValid() && openingDate.isAfter(closingDate) && (
          <div style={{ color: 'red', marginBottom: '1rem' }}>
            El formulari no pot tancar { getTimeDiff(closingDate, openingDate) } abans d'obrir-se.
          </div>
        )
      }

      {
        openingDate?.isValid() && closingDate?.isValid() && openingDate.isBefore(closingDate) && (
          <div style={{ color: 'green', marginBottom: '1rem' }}>
            El formulari estarà obert { getTimeDiff(openingDate, closingDate) }
          </div>
        )
      }

      <div style={{ gap: 20, marginBottom: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
        <div>Obriment</div>
        <div>Tancament</div>
      </div>

      <div style={{ gap: 20, marginBottom: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <input
          type="date"
          value={openingDate && applyInverseTimeZone(openingDate).toISOString() ? applyInverseTimeZone(openingDate).toISOString().split('T')[0] : ''}
          onChange={(e) => setOpeningDate(
            prevDate => {
              if (!e.target.value) return null;
              const newDate = moment(e.target.value);
              const prevMom = prevDate?.isValid() ? moment(prevDate) : newDate

              const updated = prevMom
                .year(newDate.year())
                .month(newDate.month())
                .date(newDate.date());

              // If creating the opening date, default time to 00:00
              if (!prevDate?.isValid()) {
                updated.hour(0).minute(0);
              }

              return updated;
            }
          )}
          style={{
            flex: 1,
            backgroundColor: openingDate && applyInverseTimeZone(openingDate).toISOString() ? 'lightgreen' : '#eee',
            color: 'black',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
            fontSize: 14,
          }}
        />

        <input
          type="date"
          value={closingDate && applyInverseTimeZone(closingDate).toISOString() ? applyInverseTimeZone(closingDate).toISOString().split('T')[0] : ''}
          onChange={(e) => setClosingDate(
            prevDate => {
              if (!e.target.value) return null;
              const newDate = moment(e.target.value);
              const prevMom = prevDate?.isValid() ? moment(prevDate) : newDate

              const updated = prevMom
                .year(newDate.year())
                .month(newDate.month())
                .date(newDate.date());

              // If creating the closing date, default time to 23:59
              if (!prevDate?.isValid()) {
                updated.hour(23).minute(59);
              }

              return updated;
            }
          )}
          style={{
            flex: 1,
            backgroundColor: closingDate && applyInverseTimeZone(closingDate).toISOString() ? 'lightgreen' : '#eee',
            color: 'black',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
            fontSize: 14,
          }}
        />
      </div>

      { (closingDate || openingDate) && <div style={{ gap: 20, marginBottom: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <button
          onClick={() => setOpenOpeningDateDialog(true)}
          style={{
            flex: 1,
            backgroundColor: openingDate && applyInverseTimeZone(openingDate).toISOString() ? 'lightgreen' : '#eee',
            color: 'black'
          }}
        >
          {openingDate && applyInverseTimeZone(openingDate).toISOString() ? openingDate.format('HH:mm') : '--:--'}
        </button>

        <button
          onClick={() => setOpenClosingDateDialog(true)}
          style={{
            flex: 1,
            backgroundColor: closingDate && applyInverseTimeZone(closingDate).toISOString() ? 'lightgreen' : '#eee',
            color: 'black'
          }}
        >
          {closingDate && applyInverseTimeZone(closingDate).toISOString() ? closingDate.format('HH:mm') : '--:--'}
        </button>
      </div> }

      {
        (closingDate || openingDate) && (
          <div style={{ display: 'flex', gap: 20, flexDirection: 'row', marginBottom: '1rem' }}>
            <button
              onClick={() => {
                setOpeningDate(null);
              }}
              style={{
                flex: 1,
                backgroundColor: '#eee',
                color: (openingDate === null) ? '#aaa' : 'black'
              }}
              disabled={openingDate === null}
            >
              Esborra obriment
            </button>

            <button
              onClick={() => {
                setClosingDate(null);
              }}
              style={{
                flex: 1,
                backgroundColor: '#eee',
                color: (closingDate === null) ? '#aaa' : 'black'
              }}
              disabled={closingDate === null}
            >
              Esborra tancament
            </button>
          </div>
        )
      }

      {
        ((!closingDate || closingDate.isAfter(moment())) && !isNew) &&
        <button
          onClick={() => {
            setClosingDate(moment());
          }}
          style={{
            cursor: 'pointer',
            width: '100%',
            fontSize: 16,
          }}
        >
          Tanca el formulari ara
        </button>
      }

      <button
        onClick={() => setRequired(prev => !prev)}
        style={{
          cursor: 'pointer',
          marginTop: '1rem',
          width: '100%',
          fontSize: 16,
          backgroundColor: '#eee',
          color: 'black'
        }}
      >
        {
          required ? <><input type="checkbox" checked disabled /> El formulari és obligatori</> :
          <><input type="checkbox" checked={false} disabled /> El formulari no és obligatori</>
        }
      </button>

        <button
          onClick={() => setHidden(prev => !prev)}
          style={{
            cursor: 'pointer',
            marginTop: '1rem',
            width: '100%',
            fontSize: 16,
            backgroundColor: '#eee',
            color: 'black'
          }}
        >
          {
            hidden ? <><input type="checkbox" checked={false} disabled /> El formulari està ocult</> :
            <><input type="checkbox" checked={true} disabled /> El formulari és visible</>
          }
        </button>

        <PaymentOptions payment={payment} onChange={setPayment} stripeConfigured={stripeConfigured} hasPrices={hasPrices} title={title} />

        { orderedElements.length > 0 && <ModeChanger modeReorder={modeReorder} setModeReorder={setModeReorder} /> }

      { orderedElements.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Encara no hi ha preguntes</div> }
      { modeReorder === false && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15 }}>{ElementsToEdit}</div> }
      { modeReorder === true && <ModeReorder elements={orderedElements} order={orderedElements.map(el => el.id)} setOrder={setOrder} /> }

      { 
        modeReorder === false && 
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 25 }}>
          <button
            onClick={() => setClosedDuplicateFormPopup(false)}
            style={{
              cursor: 'pointer',  
              marginBottom: '1rem',
              width: '100%',
              fontSize: 14,
            }}
          >
            + Importar preguntes d'un altre formulari
          </button>
        </div>
      }

      <AddElementButtons addElement={addElement} showImageUpload={getSubdomain() === 'falconspiera'} />
      { showLoadSaveButtons && <LoadSaveButtons saveForm={saveForm} loadForm={loadForm} /> }
      { !isNew && <button className='delete-button' onClick={deleteForm}>Esborra el formulari</button> }
    </div>
  );
};

const ModeChanger = ({ modeReorder, setModeReorder }) => {
    return (
        <div style={{ marginTop: 20, marginBottom: 20 }}>
            <button style={{ width: '100%', fontSize: 14 }} onClick={() => setModeReorder(!modeReorder)}>
                { modeReorder === false ? "Edita l'ordre" : 'Fet' }
            </button>
        </div>
    )
}

const ModeReorder = ({ elements, order, setOrder }) => {
    const orderIndexes = [
        ...order
            .map(id => elements.findIndex(element => element.id === id))
            .filter(index => index !== -1),

        ...elements
            .filter(element => !order.includes(element.id))
            .map((_, index) => index + order.length)
    ];

    const moveButtonStyle = {
        border: '1px solid #ddd',
        borderRadius: 4,
        background: '#f5f5f5',
        cursor: 'pointer',
        color: '#333',
        fontSize: 24,
        padding: '6px 12px',
        lineHeight: '24px',
        minWidth: 40,
        minHeight: 40,
    };

    const buildFullOrder = () => ([
        ...order,
        ...elements
            .filter(element => !order.includes(element.id))
            .map(element => element.id),
    ]);

    const moveItem = (id, direction) => {
        const fullOrder = buildFullOrder();
        const currentIndex = fullOrder.indexOf(id);
        if (currentIndex === -1) return;

        const delta = direction === 'up' ? -1 : 1;
        const targetIndex = currentIndex + delta;
        if (targetIndex < 0 || targetIndex >= fullOrder.length) return;

        const updated = [...fullOrder];
        const [moved] = updated.splice(currentIndex, 1);
        updated.splice(targetIndex, 0, moved);
        setOrder(updated);
    };

    const handleReorder = (newOrder) => {
        setOrder(
            newOrder
                .map(index => elements[index].id)
        )
    };

    const readableType = (type) => {
        switch (type) {
            case 'multiple-choice': return 'Única opció';
            case 'checkbox': return 'Múltiples opcions';
            case 'short-answer': return 'Resposta curta';
            case 'paragraph': return 'Resposta llarga';
            case 'text': return 'Text';
            case 'image-upload': return 'Penjar imatge';
            case 'image': return 'Imatge';
            case 'copiable-text': return 'Text per copiar';
            case 'resum-pagaments': return 'Resum pagaments';
            case 'ticket': return 'Pagament sí/no';
            case 'ticket-v2-single': return 'Pagament';
            case 'ticket-v2': return 'Pagament amb quantitats';
            case 'assistance': return 'Assistència';
            case 'stripe-payment': return 'Stripe';
            default: return 'Desconegut';
        }
    };

    return elements.length > 0 &&
        <List
            rowHeight={150}
            order={orderIndexes}
            onReOrder={handleReorder}
        >{
            elements.map((element, index) =>
                <List.Item
                    key={index}
                    as={PreguntaItem}
                    dragHandle={<DragHandle/>}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 3 }}>
                    <div style={{ flex: 1 }}>
                      <span className='type-label'>{readableType(element.type)}</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', width: '100%', flex: 2 }}>
                      <div style={{ flex: 2, fontSize: 18 }}>{
                          element.type === 'multiple-choice' ? element.content.question :
                          element.type === 'checkbox' ? element.content.question :
                          element.type === 'short-answer' ? element.content.question :
                          element.type === 'paragraph' ? element.content.question :
                          element.type === 'text' ? element.content :
                          element.type === 'image' ? 'Imatge' :
                          element.type === 'image-upload' ? element.content.question :
                          element.type === 'copiable-text' ? element.content.question :
                          element.type === 'resum-pagaments' ? 'Resum dels pagaments' :
                          element.type === 'ticket' ? element.content.question :
                          element.type === 'ticket-v2' ? element.content.question :
                          element.type === 'ticket-v2-single' ? element.content.question :
                          element.type === 'stripe-payment' ? (element.content?.buttonText || 'Pagar amb targeta') :
                          null
                      }</div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', flex: 2 }}>
                        {
                            element.type === 'multiple-choice' ? element.content.options.map((option, optionIndex) => <span key={`${element.id}-option-${optionIndex}`} className='option-label'>{option}</span>) :
                            element.type === 'checkbox' ? element.content.options.map((option, optionIndex) => <span key={`${element.id}-option-${optionIndex}`} className='option-label'>{option}</span>) :
                            element.type === 'ticket' ? element.content.options.map((option, optionIndex) => <span key={`${element.id}-option-${optionIndex}`} className='option-label'>{option}</span>) :
                            element.type === 'ticket-v2' ? element.content.options.filter(option => !option.hidden).map((option, optionIndex) => <span key={`${element.id}-option-${optionIndex}`} className='option-label'>{getTicketOptionSummary(option, optionIndex)}</span>) :
                            element.type === 'ticket-v2-single' ? element.content.options.filter(option => !option.hidden).map((option, optionIndex) => <span key={`${element.id}-option-${optionIndex}`} className='option-label'>{getTicketOptionSummary(option, optionIndex)}</span>) :
                            null
                        }
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 8, marginLeft: 10 }}>
                    <button
                      onClick={() => moveItem(element.id, 'up')}
                      disabled={orderIndexes.indexOf(index) === 0}
                      style={{
                        ...moveButtonStyle,
                        opacity: orderIndexes.indexOf(index) === 0 ? 0.5 : 1,
                      }}
                      title="Moure amunt"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveItem(element.id, 'down')}
                      disabled={orderIndexes.indexOf(index) === orderIndexes.length - 1}
                      style={{
                        ...moveButtonStyle,
                        opacity: orderIndexes.indexOf(index) === orderIndexes.length - 1 ? 0.5 : 1,
                      }}
                      title="Moure avall"
                    >
                      ↓
                    </button>
                  </div>
                </List.Item>
            )
        }</List>
}

const TitleInput = ({ title, handleTitleChange }) => {
  const onChange = (event) => handleTitleChange(event.target.value);

  return (
    <div className="form-editor-title">
      <input value={title} type="text" placeholder="Títol del formulari" onChange={onChange} />
    </div>
  );
};

const DescriptionInput = ({ description, handleDescriptionChange }) => {
  const onChange = (event) => handleDescriptionChange(event.target.value);

  return (
    <div className="form-editor-description">
      <textarea
        rows="3"
        placeholder="Descripció del formulari"
        onChange={onChange}
        value={description}
      >
      </textarea>

      <p style={{ fontSize: 12, fontStyle: 'italic' }}>
        Podeu posar <b>*negretes*</b> i <i>_cursives_</i> posant asteriscs o guions baixos al voltant del text.
      </p>
    </div>
  );
};

// The payment page Aleta makes when there is something to pay: how to pay (card
// or transfer), the IBAN of the quotes settings, the concept and the receipt.
const paymentCardStyles = {
  card: { width: '100%', boxSizing: 'border-box', border: '1px solid #e2e5ea', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '6px 14px 12px', textAlign: 'left', fontSize: 14, backgroundColor: '#f8f9fb' },
  row: { display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 0', borderTop: '1px solid #e9ecf1' },
  method: { width: 120, flexShrink: 0, fontWeight: 600 },
  off: { color: '#8a9099' },
  warning: { marginTop: 10, color: '#9a5b12' },
  linkButton: { background: 'none', border: 'none', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 },
  input: { padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #cfd5dd' },
  hint: { fontSize: 12, color: '#6b7280' },
  error: { fontSize: 12, color: '#c0392b' },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10, paddingTop: 10, borderTop: '1px solid #e9ecf1' },
  settings: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  subheading: { fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 },
  check: { display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' },
};

// This form's payment options, as the backend keeps them (form-auto-payment.js)
const COMMISSION_MODES = ['user_pays', 'colla_absorbs'];
export const normalizePayment = (payment) => {
  const concept = String(payment?.concept || '').trim();
  const commissionMode = COMMISSION_MODES.includes(payment?.commissionMode) ? payment.commissionMode : null;
  return {
    accounts: (Array.isArray(payment?.accounts) ? payment.accounts : [])
      .map((account) => ({ name: String(account?.name || '').trim(), iban: String(account?.iban || '').trim() }))
      .filter((account) => account.iban),
    cash: Boolean(payment?.cash),
    ...(concept ? { concept } : {}),
    ...(commissionMode ? { commissionMode } : {}),
    // Turned off or optional: only kept when they differ from the default
    ...(payment?.transfer === false ? { transfer: false } : {}),
    ...(payment?.card === false ? { card: false } : {}),
    ...(payment?.optionalTransferReceipt === true ? { optionalTransferReceipt: true } : {}),
    ...(payment?.optionalCashReceipt === true ? { optionalCashReceipt: true } : {}),
  };
};
// What the transfer says unless the form sets its own (as defaultConcept in the backend)
const defaultConcept = (title) => [String(title || '').trim(), '{nom} {cognom}'].filter(Boolean).join(' ');

// The ways to pay on the payment page Aleta adds to this form when it has prices,
// folded among the form's settings. A discreet "Edita", once unfolded, sets the
// form's own accounts (each with a name, say "Marracos" or "CaixaBank"; without
// any, the quotes IBAN), cash, the transfer concept (without one, the title and
// the name) and who pays the card commission (without it, the quotes setting). They
// are saved with the form; all but the concept come along when its questions are
// imported, and a new form starts with the last ones saved.
export const PaymentOptions = ({ payment, onChange, stripeConfigured, hasPrices, title = '' }) => {
  const [open, setOpen] = useState(false);
  const [quotesInfo, setQuotesInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ accounts: [], cash: false, concept: '', commissionMode: 'user_pays', transfer: true, card: true, optionalTransferReceipt: false, optionalCashReceipt: false });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAPI('/quotes/payment_info', data => setQuotesInfo(data || {}), false);
  }, []);

  const quotesIban = quotesInfo?.iban && !isDefaultIban(quotesInfo.iban) ? quotesInfo.iban : '';
  const own = normalizePayment(payment);
  const commissionMode = own.commissionMode || (quotesInfo?.commission_mode === 'colla_absorbs' ? 'colla_absorbs' : 'user_pays');
  const accounts = own.accounts.length > 0 ? own.accounts : (quotesIban ? [{ name: '', iban: quotesIban }] : []);
  const transferOn = own.transfer !== false;
  const cardOn = own.card !== false;
  const nothingToPayWith = Boolean(quotesInfo) && (accounts.length === 0 || !transferOn) && !(stripeConfigured && cardOn) && !own.cash;
  const receiptText = (optional) => (optional ? 'comprovant voluntari' : 'comprovant obligatori');

  const startEditing = () => {
    setDraft({
      accounts: own.accounts.length > 0 ? own.accounts : [{ name: '', iban: '' }],
      cash: own.cash,
      concept: own.concept || '',
      commissionMode,
      transfer: transferOn,
      card: cardOn,
      optionalTransferReceipt: own.optionalTransferReceipt === true,
      optionalCashReceipt: own.optionalCashReceipt === true,
    });
    setErrors({});
    setEditing(true);
  };
  const setAccount = (index, field, value) => {
    setDraft((current) => ({ ...current, accounts: current.accounts.map((account, i) => (i === index ? { ...account, [field]: value } : account)) }));
    setErrors((current) => ({ ...current, [index]: null }));
  };
  const finish = () => {
    const found = {};
    draft.accounts.forEach((account, index) => {
      if (!account.iban.trim()) return;
      const check = validateIban(account.iban);
      if (!check.valid) found[index] = check.error;
    });
    if (Object.keys(found).length > 0) { setErrors(found); return; }
    onChange(normalizePayment(draft));
    setEditing(false);
  };

  return (
    <div style={{ marginTop: '1rem', width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        style={{ cursor: 'pointer', width: '100%', fontSize: 16, backgroundColor: '#eee', color: 'black' }}
      >
        💰 Opcions de pagament {open ? '▴' : '▾'}
        {/* Seen without opening it: a form with prices that nobody could pay */}
        {hasPrices && nothingToPayWith ? <span style={{ color: '#9a5b12' }}> · cap disponible</span> : null}
      </button>
      {open && quotesInfo && editing ? (
        <div style={paymentCardStyles.card}>
          {/* The same rows as when it is read, each with what can change */}
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>🏦 Transferència</span>
            <div style={paymentCardStyles.settings}>
              <label style={paymentCardStyles.check}>
                <input type="checkbox" checked={draft.transfer} onChange={(event) => setDraft({ ...draft, transfer: event.target.checked })} />
                Es pot pagar per transferència
              </label>
              {draft.transfer ? (
                <>
                  <label style={paymentCardStyles.check}>
                    <input type="checkbox" checked={!draft.optionalTransferReceipt} onChange={(event) => setDraft({ ...draft, optionalTransferReceipt: !event.target.checked })} />
                    El comprovant és obligatori
                  </label>
                  <span style={{ ...paymentCardStyles.subheading, marginTop: 8 }}>Comptes</span>
                  {draft.accounts.map((account, index) => (
                    <div key={index}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          style={{ ...paymentCardStyles.input, width: 150 }}
                          value={account.name}
                          placeholder="Nom (opcional)"
                          aria-label="Nom del compte"
                          onChange={(event) => setAccount(index, 'name', event.target.value)}
                        />
                        <input
                          style={{ ...paymentCardStyles.input, flex: 1, minWidth: 0 }}
                          value={account.iban}
                          placeholder={index === 0 && quotesIban ? quotesIban : 'ES00 0000 0000 0000 0000 0000'}
                          aria-label="IBAN"
                          onChange={(event) => setAccount(index, 'iban', event.target.value)}
                        />
                        {draft.accounts.length > 1 ? (
                          <button
                            type="button"
                            aria-label="Treu el compte"
                            onClick={() => setDraft((current) => ({ ...current, accounts: current.accounts.filter((_, i) => i !== index) }))}
                            style={{ fontSize: 13, backgroundColor: '#e9edf3', color: '#333' }}
                          >×</button>
                        ) : null}
                      </div>
                      {errors[index] ? <span style={paymentCardStyles.error}>{errors[index]}</span> : null}
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={paymentCardStyles.hint}>
                      {quotesIban ? "Sense cap compte, es fa servir l'IBAN de quotes." : "Sense cap compte, no es pot pagar per transferència."}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDraft((current) => ({ ...current, accounts: [...current.accounts, { name: '', iban: '' }] }))}
                      style={paymentCardStyles.linkButton}
                    >+ Afegeix un compte</button>
                  </div>
                  <span style={{ ...paymentCardStyles.subheading, marginTop: 8 }}>Concepte</span>
                  <input
                    style={paymentCardStyles.input}
                    value={draft.concept}
                    placeholder={defaultConcept(title)}
                    aria-label="Concepte"
                    onChange={(event) => setDraft({ ...draft, concept: event.target.value })}
                  />
                </>
              ) : <span style={paymentCardStyles.hint}>A l'app surt en gris.</span>}
            </div>
          </div>
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>💳 Targeta</span>
            {stripeConfigured ? (
              <div style={paymentCardStyles.settings}>
                <label style={paymentCardStyles.check}>
                  <input type="checkbox" checked={draft.card} onChange={(event) => setDraft({ ...draft, card: event.target.checked })} />
                  Es pot pagar amb targeta
                </label>
                {draft.card ? (
                  <>
                    <span style={{ ...paymentCardStyles.subheading, marginTop: 8 }}>Qui paga la comissió?</span>
                    <select
                      style={paymentCardStyles.input}
                      value={draft.commissionMode}
                      aria-label="Qui paga la comissió?"
                      onChange={(event) => setDraft({ ...draft, commissionMode: event.target.value })}
                    >
                      <option value="user_pays">L'usuari paga la comissió (la colla rep l'import complet)</option>
                      <option value="colla_absorbs">La colla absorbeix la comissió (l'usuari paga l'import exacte)</option>
                    </select>
                  </>
                ) : <span style={paymentCardStyles.hint}>A l'app surt en gris.</span>}
              </div>
            ) : <span style={paymentCardStyles.off}>No activada</span>}
          </div>
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>🤝 En persona</span>
            <div style={paymentCardStyles.settings}>
              <label style={paymentCardStyles.check}>
                <input type="checkbox" checked={draft.cash} onChange={(event) => setDraft({ ...draft, cash: event.target.checked })} />
                Es pot pagar en persona
              </label>
              {draft.cash ? (
                <label style={paymentCardStyles.check}>
                  <input type="checkbox" checked={!draft.optionalCashReceipt} onChange={(event) => setDraft({ ...draft, optionalCashReceipt: !event.target.checked })} />
                  La foto del moment és obligatòria
                </label>
              ) : null}
              <span style={paymentCardStyles.hint}>En efectiu o amb datàfon. Desactivat, a l'app no surt.</span>
            </div>
          </div>
          <div style={paymentCardStyles.actions}>
            <button type="button" onClick={() => setEditing(false)} style={{ fontSize: 13, backgroundColor: '#e9edf3', color: '#333' }}>Cancel·la</button>
            <button type="button" onClick={finish} style={{ fontSize: 13 }}>Fet</button>
          </div>
        </div>
      ) : open && quotesInfo ? (
        <div style={paymentCardStyles.card}>
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>🏦 Transferència</span>
            {!transferOn ? <span style={paymentCardStyles.off}>Desactivada</span> : accounts.length > 0 ? (
              <span>
                {accounts.map((account) => (
                  <span key={account.iban} style={{ display: 'block' }}>{account.name ? `${account.name}: ` : ''}{account.iban}</span>
                ))}
                <span style={{ ...paymentCardStyles.off, display: 'block' }}>Concepte: {own.concept || defaultConcept(title)}</span>
                <span style={{ ...paymentCardStyles.off, display: 'block' }}>Amb {receiptText(own.optionalTransferReceipt)}</span>
              </span>
            ) : <span style={paymentCardStyles.off}>Sense IBAN</span>}
          </div>
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>💳 Targeta</span>
            {stripeConfigured && cardOn ? (
              <span>{commissionMode === 'colla_absorbs' ? 'Pagament al moment, la colla absorbeix la comissió' : "Pagament al moment, l'usuari paga la comissió"}</span>
            ) : <span style={paymentCardStyles.off}>{stripeConfigured ? 'Desactivada' : 'No activada'}</span>}
          </div>
          <div style={paymentCardStyles.row}>
            <span style={paymentCardStyles.method}>🤝 En persona</span>
            {own.cash ? <span>En efectiu o amb datàfon, amb foto {own.optionalCashReceipt ? 'voluntària' : 'obligatòria'}</span> : <span style={paymentCardStyles.off}>No</span>}
          </div>
          {nothingToPayWith ? (
            <p style={paymentCardStyles.warning}>Ara mateix no hi hauria pàgina de pagament.</p>
          ) : null}
          <button type="button" onClick={startEditing} style={{ width: '100%', marginTop: 10, fontSize: 14, backgroundColor: 'white', color: 'dodgerblue', border: '1px solid dodgerblue' }}>Canvia les opcions de pagament</button>
        </div>
      ) : null}
    </div>
  );
};

// A payment element the junta built before the page was automatic, kept while the form has
// answers or nothing priced (the same rule as isHandMadePaymentElement in the backend's
// form-auto-payment.js, which hides the others when the editor opens the form)
const IBAN_PATTERN = /^[A-Z]{2}\d{2}(?:\s?[A-Z0-9]{4}){3,}/i;
const IBAN_INSIDE = /\b[A-Z]{2}\d{2}(?:\s?[A-Z0-9]{4}){3,}/i;
const METHOD_WORDS = /transfer|targeta|efectiu|en persona|bizum|presencial|en m[àa]\b|met[àa]l·?lic|aleta|caixer|tpv|dat[àa]fon|sum-?up|paypal/i;
const METHOD_QUESTION = /com (?:vols |voldr[àa]s |far[àa]s |far[ée] |efectuar[àa]s )?(?:fer )?(?:el )?(?:pagar|pagar[àa]s|pagues|pagament)|m[èe]tode de pagament|amb qu[èe] pagar[àa]s|opci[óo] de pagament|^pagament\??$|pagues (?:amb|en) transf/i;
const RECEIPT_WORDS = /comprovant|pagament|pagat|transfer|cobrat|bizum|rebut/i;
const optionTexts = (element) => (Array.isArray(element?.content?.options) ? element.content.options : [])
  .map(option => String(typeof option === 'string' ? option : option?.text || '').trim()).filter(Boolean);
export const isOldPaymentElement = (element) => Boolean(element) && element.autoPayment !== true && (
  element.paymentScreen === true
  || element.type === 'stripe-payment'
  || (element.type === 'image-upload' && element.isComprovant === true
    && (RECEIPT_WORDS.test(element.content?.question || '') || IBAN_INSIDE.test(element.content?.question || '')))
  || (element.type === 'copiable-text' && IBAN_PATTERN.test(String(element.content?.text || '').trim()))
  || (['multiple-choice', 'checkbox'].includes(element.type) && optionTexts(element).length > 0
    && (optionTexts(element).every(text => METHOD_WORDS.test(text)) || METHOD_QUESTION.test(String(element.content?.question || '').trim()))));

const AddElementButtons = ({ addElement, showImageUpload }) => {
    return (
      <>
      <h3>📝 Respostes per respondre</h3>
      <div className="add-element-buttons">
        <button onClick={() => addElement('multiple-choice')}>
            + Opció única
        </button>
        <button onClick={() => addElement('checkbox')}>
            + Vàries opcions
        </button>
        <button onClick={() => addElement('assistance')}>
            + Assistència
        </button>
        <button onClick={() => addElement('short-answer')}>
            + Resposta curta
        </button>
        <button onClick={() => addElement('paragraph')}>
            + Resposta llarga
        </button>
        {showImageUpload && (
            <button onClick={() => addElement('image-upload')}>
                + Penjar imatge
            </button>
        )}
      </div>
      <h3>🖼️ Elements estàtics</h3>
      <div className="add-element-buttons">
        <button onClick={() => addElement('text')}>
            + Text
        </button>
        <button onClick={() => addElement('image')}>
            + Imatge
        </button>
        <button onClick={() => addElement('copiable-text')}>
            + Text per copiar
        </button>
      </div>
      <h3>💰 Pagaments</h3>
      <div className="add-element-buttons">
        <button onClick={() => addElement('ticket-v2-single')}>
            + Pagament (sense quantitats)
        </button>
        <button onClick={() => addElement('ticket-v2')}>
            + Pagament (amb quantitats)
        </button>
      </div>
      </>
    );
  };

  const LoadSaveButtons = ({ saveForm, loadForm }) => {
    return (
      <div className="fixed-load-save-buttons">
        <button onClick={saveForm} title="Save">
          💾
        </button>
      </div>
    )

    // return (
    //   <div className="load-save-buttons">
    //     <button onClick={saveForm} title="Save">
    //       💾 Guarda el formulari
    //     </button>
    //     <button onClick={loadForm} title="Reset">
    //       🔄 Torna a començar
    //     </button>
    //   </div>
    // );
  };  

const FormElement = ({ element, updateElement, removeElement, errors, selectedEvent, allElements, events }) => {
  const ComponentMap = {
    'multiple-choice': MultipleChoiceQuestion,
    checkbox: CheckboxQuestion,
    'short-answer': ShortAnswerQuestion,
    paragraph: ParagraphQuestion,
    'image-upload': ImageUploadQuestion,
    'copiable-text': CopiableTextQuestion,
    'resum-pagaments': ResumPagamentsQuestion,
    ticket: TicketQuestion,
    text: TextElement,
    image: ImageElement,
    'ticket-v2': TicketV2Question,
    'ticket-v2-single': TicketV2SingleQuestion,
    assistance: AssistanceQuestion,
    'stripe-payment': StripePaymentQuestion,
  };

  const Component = ComponentMap[element.type];

  const conditionalParentQuestions = getConditionalParentQuestions(allElements, element.id);

  const handleToggleConditional = (checked) => {
    if (!checked) {
      updateElement({ ...element, showIf: null });
    } else if (conditionalParentQuestions.length > 0) {
      const firstQ = conditionalParentQuestions[0];
      const hasOptions = Array.isArray(firstQ.content.options);
      const isAssistance = firstQ.type === 'assistance';
      updateElement({
        ...element,
        showIf: {
          questionId: firstQ.id,
          optionId: hasOptions ? 0 : (isAssistance ? 1 : ''),
        },
      });
    }
  };

  const handleChangeQuestion = (e) => {
    const questionId = e.target.value;
    const selectedQ = conditionalParentQuestions.find(q => q.id === questionId);
    if (!selectedQ) return;

    const hasOptions = Array.isArray(selectedQ.content.options);
    const isAssistance = selectedQ.type === 'assistance';
    updateElement({
      ...element,
      showIf: {
        questionId,
        optionId: hasOptions ? 0 : (isAssistance ? 1 : ''),
      }
    });
  };

  const handleChangeOption = (e) => {
    updateElement({
      ...element,
      showIf: {
        ...element.showIf,
        optionId: e.target.value,
      }
    });
  };

  const toggleRequired = () => {
    // Ticket is always required 
    if (element.type === 'ticket') updateElement({ ...element, required: true });
    else updateElement({ ...element, required: !element.required });
  };

  // A question the junta had put on the old payment page: back among the others
  const moveToQuestions = () => {
    updateElement({
      ...element,
      paymentScreen: false,
      showIf: null,
    });
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleRemoveFromMenu = () => {
    setMenuOpen(false);
    const isTicket = ['ticket', 'ticket-v2', 'ticket-v2-single'].includes(element.type);
    const isRemoved = removeElement();
    if (isRemoved && isTicket) {
      postAPI('/remove_tiquet', { id: element.id });
    }
  };

  return (
    <div className='form-element'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div className="form-element-menu" ref={menuRef}>
          <button className="form-element-menu-trigger" onClick={() => setMenuOpen(!menuOpen)}>
            ⋮
          </button>
          {menuOpen && (
            <div className="form-element-popover">
              {element?.paymentScreen === true ? (
                <>
                  <button onClick={() => { setMenuOpen(false); moveToQuestions(); }}>
                    Mou a les preguntes
                  </button>
                  <div className="popover-divider" />
                </>
              ) : null}
              <button className="danger" onClick={handleRemoveFromMenu}>
                Elimina
              </button>
            </div>
          )}
        </div>

        {!['text', 'image', 'copiable-text', 'resum-pagaments'].includes(element.type) &&
        <div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            {
              // If it's a pagament amb quantitats (ticket-v2)
              element.type === 'ticket-v2' && (
                <span
                  style={{
                    background: '#e0e7ff',
                    color: '#3730a3',
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 4,
                    padding: '2px 8px',
                    marginRight: 8,
                    whiteSpace: 'nowrap',
                    border: '1px solid #a5b4fc'
                  }}
                  title="Aquest pagament permet seleccionar quantitats"
                >
                  amb quantitats
                </span>
              )
            }
            <input
              type="checkbox"
              checked={element.required}
              onChange={toggleRequired}
              style={{ marginRight: 6 }}
            />
            Obligatòria
          </label>
        </div>
        }
      </div>
      {isOldPaymentElement(element) ? (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9a5b12', textAlign: 'left' }}>
          De la pàgina de pagament antiga: mentre hi sigui, l'Aleta no hi posa l'automàtica.
        </p>
      ) : null}

      <Component
        element={element}
        updateElement={updateElement}
        removeElement={removeElement}
        errors={errors}
        selectedEvent={selectedEvent}
        events={events}
      />

      {conditionalParentQuestions.length > 0 && (
        <div style={{ 
          marginTop: 20, 
          padding: 15, 
          borderRadius: 8, 
          backgroundColor: element.showIf ? '#f0f7ff' : '#f5f5f5',
          border: `1px solid ${element.showIf ? '#cce0ff' : '#e0e0e0'}`,
          transition: 'all 0.2s ease-in-out'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginBottom: element.showIf ? 15 : 0 
          }}>
            <input
              type="checkbox"
              id={`conditional-${element.id}`}
              checked={!!element.showIf}
              onChange={(e) => handleToggleConditional(e.target.checked)}
              style={{ 
                marginRight: 10,
                width: 18,
                height: 18
              }}
            />
            <label 
              htmlFor={`conditional-${element.id}`}
              style={{ 
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={{ marginRight: 8 }}>Pregunta condicionada</span>
              {element.showIf && (
                <span style={{ 
                  backgroundColor: '#007bff', 
                  color: 'white', 
                  fontSize: 12, 
                  padding: '2px 6px', 
                  borderRadius: 10,
                  fontWeight: 'normal'
                }}>
                  Activa
                </span>
              )}
            </label>
          </div>

          {element.showIf && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 12
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 5,
                  fontSize: 13,
                  color: '#555'
                }}>
                  Mostra aquesta pregunta només quan la pregunta:
                </label>
                <select 
                  value={element.showIf.questionId} 
                  onChange={handleChangeQuestion}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    fontSize: 14
                  }}
                >
                  {conditionalParentQuestions.map(q => (
                    <option key={q.id} value={q.id}>{q.content.question}</option>
                  ))}
                </select>
              </div>
              
              {Array.isArray(conditionalParentQuestions.find(q => q.id === element.showIf.questionId)?.content.options) ? (
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: 5,
                    fontSize: 13,
                    color: '#555'
                  }}>
                    Té el valor:
                  </label>
                  <select 
                    value={element.showIf.optionId} 
                    onChange={handleChangeOption}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 14
                    }}
                  >
                    {conditionalParentQuestions.find(q => q.id === element.showIf.questionId).content.options.map((op, idx) => (
                      <option key={idx} value={idx}>{getOptionDisplayText(op, idx)}</option>
                    ))}
                  </select>
                </div>
              ) : conditionalParentQuestions.find(q => q.id === element.showIf.questionId)?.type === 'assistance' ? (
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: 5,
                    fontSize: 13,
                    color: '#555'
                  }}>
                    Té el valor:
                  </label>
                  <select 
                    value={element.showIf.optionId} 
                    onChange={handleChangeOption}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 14
                    }}
                  >
                    <option value={1}>{conditionalParentQuestions.find(q => q.id === element.showIf.questionId)?.content.yesLabel || 'Sí'}</option>
                    <option value={0}>{conditionalParentQuestions.find(q => q.id === element.showIf.questionId)?.content.noLabel || 'No'}</option>
                  </select>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FormEditor;
